import React, { useMemo, useState } from 'react';
import { View, StyleSheet, Image } from 'react-native';
import { Text } from '@/components/common/text';
import { useAppSelector } from '@/store';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { WebConfirmModal, ModalThemeColors } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { DesktopSectionHeader } from '@/components/residents/desktop/myAccount/DesktopSectionHeader';
import { useConfirmModal } from '@/hooks/common/useConfirmModal';
import { useDeleteAccountMutation } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { strings } from '@/constants/residents/strings';
import { useThemedStyles, useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';

function useStyles() {
  return useThemedStyles(({ colors, semantic, components }) =>
    StyleSheet.create({
      root: {
        flex: 1,
        backgroundColor: colors.bg.base,
      },
      header: {
        borderBottomWidth: 1,
        borderBottomColor: colors.text.subtle,
        paddingHorizontal: 20,
        paddingVertical: 20,
      },
      headerTitle: {
        color: colors.text.primary,
        fontWeight: '800',
        fontSize: 18,
      },
      body: {
        flex: 1,
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
      },
      avatarColumn: {
        alignItems: 'flex-start',
        gap: 14,
      },
      avatarCircle: {
        width: 72,
        height: 72,
        borderRadius: 36,
        overflow: 'hidden',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: components.avatar.background,
      },
      avatarImg: {
        width: 72,
        height: 72,
      },
      logoContainer: {
        position: 'absolute',
        right: 0,
        bottom: 0,
      },
      logoInner: {
        position: 'absolute',
        right: -30,
        bottom: -50,
        opacity: 0.3,
      },
      nameText: {
        color: colors.text.primary,
        fontSize: 18,
        fontWeight: '600',
      },
      emailText: {
        color: colors.text.primary,
        opacity: 0.9,
        marginTop: 2,
      },
      footer: {
        paddingHorizontal: 20,
        paddingBottom: 16,
        marginTop: 'auto',
      },
      deleteLink: {
        color: semantic.icon.inactive,
        fontSize: 12,
        fontWeight: '500',
      },
      // Underline only: the `link` kind would move this muted ink to the brand
      // blue, which a destructive action must not borrow.
      deleteLinkHovered: {
        textDecorationLine: 'underline',
      },
    }),
  );
}

function useModalThemeColors(): ModalThemeColors {
  const { colors, semantic } = useThemeColors();
  return {
    overlay: 'rgba(0,0,0,0.5)',
    modalBg: colors.card.bg,
    modalBorder: colors.card.border,
    titleText: colors.text.primary,
    messageText: colors.text.secondary,
    cancelBg: colors.bg.base,
    cancelBorder: colors.card.border,
    cancelText: colors.text.primary,
    confirmBg: semantic.interactive.danger,
    confirmText: semantic.text.inverse,
  };
}

export const AccountInformationView: React.FC = () => {
  const styles = useStyles();
  const modalColors = useModalThemeColors();
  const user = useAppSelector((s) => (s as any).auth?.user);
  const name = useMemo(() => {
    const first = user?.firstName || '';
    const last = user?.lastName || '';
    const full = `${first} ${last}`.trim();
    return full.length > 0 ? full : strings.account.guestAccountName;
  }, [user?.firstName, user?.lastName]);

  const email = user?.email || '';
  const photoUrl = (user as any)?.profilePictureUrl || '';

  const { visible: confirmVisible, open: openConfirm, close: closeConfirm } = useConfirmModal(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteAccount] = useDeleteAccountMutation();
  const { showSuccess, showError } = useToast();

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      const resp = await deleteAccount().unwrap();
      if ( (resp as any)?.success ) {
        closeConfirm();
        showSuccess('Account deleted');
      } else {
        closeConfirm();
        showError('Failed to delete account');
      }
    } catch (_e) {
      closeConfirm();
      showError('Failed to delete account');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View style={styles.root}>
      <DesktopSectionHeader
        title={strings.account.informationTitle}
        containerStyle={styles.header}
        titleStyle={styles.headerTitle}
      />
      <View style={styles.body}>
        <View style={styles.avatarColumn}>
          <View style={styles.avatarCircle}>
            {photoUrl ? (
              <Image source={{ uri: photoUrl }} style={styles.avatarImg} />
            ) : (
              <RESIDENTS_SVG_COMPONENTS.PROFILE_PLACEHOLDER width={36} height={36} />
            )}
          </View>
          <Text style={styles.nameText}>{name}</Text>
          <Text style={styles.emailText}>{email}</Text>
        </View>
        <View pointerEvents="none" style={styles.logoContainer}>
          <View style={styles.logoInner}>
            <RESIDENTS_SVG_COMPONENTS.RESIO_LOGO_FADED width={160} height={190} />
          </View>
        </View>
      </View>

      <View style={styles.footer}>
        <Hoverable hover="none" accessibilityRole="button" onPress={openConfirm}>
          {({ hovered }) => (
            <Text style={[styles.deleteLink, hovered && styles.deleteLinkHovered]}>{strings.account.deleteAccountLink}</Text>
          )}
        </Hoverable>
      </View>

      <WebConfirmModal
        visible={confirmVisible}
        title={strings.account.deleteAccountConfirmTitle}
        message={strings.account.deleteAccountConfirmMessage}
        confirmText={strings.alert.delete}
        cancelText={strings.alert.cancel}
        onConfirm={handleDelete}
        onCancel={closeConfirm}
        themeColors={modalColors}
      />
    </View>
  );
};

export default AccountInformationView;
